import React from "react";
import { cn } from "../../lib/utils";
import type {
  MapViewerFiltersSheetBodyProps,
  MapViewerFilterItem,
} from "../../contracts/map-viewer";
import { Switch } from "../../internal/primitives/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../internal/primitives/select";
import { Input } from "../../internal/primitives/input";
import { Slider } from "../../internal/primitives/slider";
import { Label } from "../../internal/primitives/label";

export function MapViewerFiltersSheetBody({
  title,
  description,
  sections,
  emptyMessage = "No filters available",
  resetAllLabel = "Reset all filters",
  onEnabledChange,
  onValueChange,
  onResetFilter,
  onResetAll,
  className,
}: MapViewerFiltersSheetBodyProps) {
  const renderValueEditor = (item: MapViewerFilterItem) => {
    const { control, id, enabled, canEditValue } = item;
    const isDisabled = !enabled || canEditValue === false;

    switch (control.kind) {
      case "boolean":
        // Boolean filters use their authored predicate toggle directly 
        // and should not render a duplicate enable switch.
        return (
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
              {control.enabledLabel || (control.value ? "Active" : "Inactive")}
            </span>
            <Switch
              id={id}
              checked={control.value}
              onCheckedChange={(checked) => onValueChange(id, checked)}
              disabled={canEditValue === false}
            />
          </div>
        );

      case "single-select":
        return (
          <Select
            value={control.value}
            onValueChange={(val) => onValueChange(id, val)}
            disabled={isDisabled}
          >
            <SelectTrigger id={id} className="h-8 text-xs bg-background">
              <SelectValue placeholder={control.placeholder || "Select option"} />
            </SelectTrigger>
            <SelectContent>
              {control.options.map((opt) => (
                <SelectItem key={opt.id} value={opt.id} disabled={opt.disabled} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "multi-select":
        return (
          <div className={cn("flex flex-wrap gap-1.5", !enabled && "opacity-50 grayscale-[0.5]")}>
            {control.options.map((opt) => {
              const isActive = control.value.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  disabled={isDisabled || opt.disabled}
                  onClick={() => {
                    const next = isActive
                      ? control.value.filter((v) => v !== opt.id)
                      : [...control.value, opt.id];
                    onValueChange(id, next);
                  }}
                  className={cn(
                    "px-2 py-1 text-[10px] font-medium rounded-full border transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground border-transparent"
                      : "bg-background text-muted-foreground border-border hover:bg-muted/50",
                    (isDisabled || opt.disabled) && "cursor-not-allowed"
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        );

      case "numeric-range":
        if (control.presentation === "max-slider") {
          return (
            <div className={cn("space-y-3", !enabled && "opacity-50 grayscale-[0.5]")}>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground uppercase">Maximum</Label>
                <Input
                  type="number"
                  value={control.value.max ?? ""}
                  placeholder={control.maxPlaceholder ?? "None"}
                  onChange={(e) => {
                    const val = e.target.value === "" ? null : Number(e.target.value);
                    onValueChange(id, { min: null, max: val });
                  }}
                  disabled={isDisabled}
                  className="h-7 text-xs font-mono"
                />
              </div>
              {control.showSlider && control.minBound !== null && control.maxBound !== null && (
                <Slider
                  value={[control.value.max ?? control.maxBound ?? 0]}
                  min={control.minBound ?? 0}
                  max={control.maxBound ?? 100}
                  step={control.step ?? 1}
                  onValueChange={([max]) => onValueChange(id, { min: null, max: max ?? null })}
                  disabled={isDisabled}
                  className="py-1"
                />
              )}
            </div>
          );
        }

        return (
          <div className={cn("space-y-3", !enabled && "opacity-50 grayscale-[0.5]")}>
            <div className="flex items-center gap-2">
              <div className="flex-1 space-y-1">
                <Label className="text-[10px] text-muted-foreground uppercase">Min</Label>
                <Input
                  type="number"
                  value={control.value.min ?? ""}
                  placeholder={control.minPlaceholder ?? "None"}
                  onChange={(e) => {
                    const val = e.target.value === "" ? null : Number(e.target.value);
                    onValueChange(id, { ...control.value, min: val });
                  }}
                  disabled={isDisabled}
                  className="h-7 text-xs font-mono"
                />
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-[10px] text-muted-foreground uppercase">Max</Label>
                <Input
                  type="number"
                  value={control.value.max ?? ""}
                  placeholder={control.maxPlaceholder ?? "None"}
                  onChange={(e) => {
                    const val = e.target.value === "" ? null : Number(e.target.value);
                    onValueChange(id, { ...control.value, max: val });
                  }}
                  disabled={isDisabled}
                  className="h-7 text-xs font-mono"
                />
              </div>
            </div>
            {control.showSlider && control.minBound !== null && control.maxBound !== null && (
              <Slider
                value={[control.value.min ?? control.minBound ?? 0, control.value.max ?? control.maxBound ?? 100]}
                min={control.minBound ?? 0}
                max={control.maxBound ?? 100}
                step={control.step ?? 1}
                onValueChange={([min, max]) => onValueChange(id, { min, max })}
                disabled={isDisabled}
                className="py-1"
              />
            )}
          </div>
        );

      case "text":
        return (
          <Input
            value={control.value}
            onChange={(e) => onValueChange(id, e.target.value)}
            placeholder={control.placeholder || "Filter text..."}
            className="h-8 text-xs bg-background"
            disabled={isDisabled}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={cn("flex flex-col h-full bg-card", className)}>
      <header className="px-4 py-4 shrink-0 border-b border-border">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-sm font-bold tracking-tight">{title || "Filters"}</h2>
          {onResetAll && (
            <button
              onClick={onResetAll}
              className="text-[10px] font-bold uppercase tracking-wider text-primary hover:opacity-80 transition-opacity"
            >
              {resetAllLabel}
            </button>
          )}
        </div>
        {description && (
          <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-8">
        {sections.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-12 italic border border-dashed rounded-lg">
            {emptyMessage}
          </p>
        ) : (
          sections.map((section) => (
            <div key={section.id} className="space-y-4">
              <div className="flex flex-col border-l-2 border-primary/20 pl-3">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                  {section.title}
                </h3>
                {section.description && (
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                    {section.description}
                  </p>
                )}
              </div>

              <div className="space-y-6">
                {section.items.map((item) => (
                  <div key={item.id} className="group relative">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <Label className={cn(
                            "text-xs font-semibold transition-colors",
                            item.enabled ? "text-foreground" : "text-muted-foreground"
                          )}>
                            {item.label}
                          </Label>
                          {onResetFilter && item.enabled && (
                            <button
                              onClick={() => onResetFilter(item.id)}
                              className="text-[9px] font-bold uppercase text-muted-foreground hover:text-primary transition-colors opacity-0 group-hover:opacity-100"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                        {item.helperText && (
                          <p className="text-[10px] text-muted-foreground/70 leading-snug">
                            {item.helperText}
                          </p>
                        )}
                      </div>
                      
                      {item.control.kind !== "boolean" && item.canToggleEnabled !== false && (
                        <Switch
                          checked={item.enabled}
                          onCheckedChange={(checked) => onEnabledChange?.(item.id, checked)}
                          className="mt-0.5"
                        />
                      )}
                    </div>
                    
                    <div className={cn(
                      "rounded-lg transition-all duration-200",
                      item.control.kind !== "boolean" && "bg-muted/20 p-2.5 border border-transparent group-hover:border-border",
                      !item.enabled && item.control.kind !== "boolean" && "bg-muted/5"
                    )}>
                      {renderValueEditor(item)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
